import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AnnouncementForm from '../../components/announcements/AnnouncementForm';
import {
  announcementsApi, errorOf, announceChanged, IMPORTANCE_LABEL, STATE_LABEL, stateTone, when, doneWords
} from '../../lib/announcements';
import '../../components/announcements/Announcements.css';

/**
 * ADVANCED -> ANNOUNCEMENTS (125). Administrators.
 *
 * Under Advanced rather than Settings because an announcement is work, not
 * a setting: written, followed up, reminded about - the same reason
 * Joiners & leavers sits there and Ticket templates live outside Settings.
 * (Settings -> Chat, calls & meetings has a door here, so its search finds
 * it.)
 *
 * The list, newest start first, filtered by where each stands (showing,
 * scheduled, ended - in the address, so a filtered list can be sent), each
 * with who it is for and how many have read or acknowledged it; "Write an
 * announcement" opens the form. A row opens the announcement's own page.
 */
const SHOWS = [['', 'All'], ['live', 'Showing'], ['scheduled', 'Scheduled'], ['ended', 'Ended']];

export default function AnnouncementsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [writing, setWriting] = useState(false);
  const latest = useRef(0);

  const load = useCallback(() => {
    const n = ++latest.current;
    return announcementsApi.list()
      .then((r) => { if (n === latest.current) { setData(r); setError(''); } })
      .catch((e) => {
        if (n !== latest.current) return;
        setError(e?.response?.data?.missing === 'announcements'
          ? 'Announcements are not set up on this server yet: they need migration 125 (npm run migrate:125).'
          : errorOf(e, 'Could not load the announcements.'));
      });
  }, []);
  useEffect(() => { load(); }, [load]);

  const show = params.get('show') || '';
  // From the address as it is this moment, not the router's lagging copy.
  const setShow = (v) => {
    const next = new URLSearchParams(window.location.search);
    if (v) next.set('show', v); else next.delete('show');
    setParams(next, { replace: true });
  };
  const all = data?.announcements || [];
  const rows = show ? all.filter((a) => a.state === show) : all;
  const count = (s) => all.filter((a) => a.state === s).length;
  const close = useCallback(() => setWriting(false), []);

  return (
    <div className="container ann-page" data-testid="announcements-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Announcements</h1>
          <p className="page-description">
            Tell everybody - or staff, contractors, a department or a few people - something. Info goes under the bell;
            Important is a banner on every page until each person has read it.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setWriting(true)}>Write an announcement</button>
      </div>

      {error && <p className="ann-problem" role="alert">{error}</p>}
      {!data && !error && <p className="ann-quiet" aria-busy="true">Loading the announcements…</p>}

      {data && (
        <>
          <div className="ann-seg" role="group" aria-label="Show">
            {SHOWS.map(([v, l]) => (
              <button key={v || 'all'} type="button" aria-pressed={show === v} onClick={() => setShow(v)}>
                {v ? `${l} (${count(v)})` : `${l} (${all.length})`}
              </button>
            ))}
          </div>
          {rows.length === 0 ? (
            <p className="ann-quiet" data-testid="announcements-empty">
              {all.length ? 'None here.' : 'No announcements yet. Write the first one.'}
            </p>
          ) : (
            <ul className="ann-list">
              {rows.map((a) => (
                <li key={a.id}>
                  <Link className="ann-row" to={`/announcements/${a.id}`} data-announcement={a.id}>
                    <span className="ann-row-title">
                      {a.importance === 'important' && <span className="ann-chip is-important">{IMPORTANCE_LABEL.important}</span>}
                      {a.title}
                      <span className="ann-chip" data-tone={stateTone(a.state)}>{STATE_LABEL[a.state]}</span>
                      {a.source === 'gdpr_notice' && <span className="ann-chip">Staff notice</span>}
                    </span>
                    <span className="ann-row-count">{doneWords(a)}</span>
                    <span className="ann-row-sub">
                      {`For ${a.audience_words} · ${a.state === 'scheduled' ? 'starts' : 'started'} ${when(a.starts_at)}`}
                      {a.ends_at ? ` · ends ${when(a.ends_at)}` : ''}
                      {a.version > 1 ? ` · version ${a.version}` : ''}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {writing && (
        <AnnouncementForm onClose={close} onSaved={(r) => {
          setWriting(false);
          announceChanged();
          navigate(`/announcements/${r.announcement.id}`);
        }} />
      )}
    </div>
  );
}
